import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import loginImage from '../assets/images/login-image.png';

const BRAND_GRADIENT = "linear-gradient(135deg, #0047ff, #00c4ff)";

/* ================= WRAPPER ================= */

const Wrapper = styled.div`
  font-family: 'Poppins', sans-serif;
  min-height: 100vh;
  color: #e5e7eb;
  padding: 80px 16px;
  overflow-x: hidden;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

/* ================= HERO ================= */

const Section = styled(motion.section)`
  text-align: center;
  padding: 40px 20px;
`;

const Title = styled.h1`
  font-size: 2.4rem;
  margin-bottom: 20px;
  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Paragraph = styled.p`
  max-width: 900px;
  margin: 0 auto 30px;
  font-size: 1.1rem;
  line-height: 1.65;
  color: #cbd5e1;
`;

const CtaButton = styled(motion.button)`
  padding: 14px 34px;
  border-radius: 50px;
  border: none;
  background: ${BRAND_GRADIENT};
  color: #fff;
  font-weight: 600;
  font-size: 1.05rem;
  cursor: pointer;

  @media (max-width: 425px) {
    width: 100%;
    max-width: 320px;
  }
`;

/* ================= GRID ================= */

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 40px;
  margin-top: 60px;
  justify-items: center;
  align-items: start;

  @media (max-width: 425px) {
    gap: 30px;
  }
`;

const Image = styled.img`
  width: 240px;
  border-radius: 14px;
  margin-top: 20px;

  @media (max-width: 425px) {
    width: 200px;
  }
`;

const Card = styled(motion.div)`
  width: 100%;
  max-width: 420px;
  background: rgba(255,255,255,0.08);
  backdrop-filter: blur(14px);
  border-radius: 20px;
  padding: 28px;
  border: 1px solid rgba(255,255,255,0.12);
  box-shadow: 0 10px 30px rgba(0,0,0,0.25);

  @media (max-width: 425px) {
    text-align: center;
  }
`;

const CardTitle = styled.h2`
  margin-bottom: 12px;
  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 425px) {
    text-align: center;
  }
`;

const CardDesc = styled.p`
  font-size: 0.95rem;
  line-height: 1.65;
  color: #cbd5e1;
`;

const CardBtn = styled(motion.button)`
  margin-top: 20px;
  background: ${BRAND_GRADIENT};
  color: #fff;
  padding: 10px 22px;
  border: none;
  border-radius: 25px;
  font-weight: 500;
  cursor: pointer;

  @media (max-width: 425px) {
    margin: 20px auto 0;
  }
`;

/* ================= COMPONENT ================= */

export default function CaseStudy() {
  const [showFullDesc, setShowFullDesc] = useState(false);

  return (
    <Wrapper>
      <Container>

        <Section
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Title>Case Study</Title>

          <Paragraph>
            "Cultivating fully-optimized web applications tailored to businesses of all sizes and complexities, Berrybloomer leverages cutting-edge technologies to craft solutions that exceed expectations. From intuitive designs to intricate functionalities, our Development Company is committed to delivering web solutions that propel businesses toward success. With a focus on innovation and utilizing the most worthy technologies, we ensure that each project is a testament to our expertise and dedication to excellence."
          </Paragraph>

          <CtaButton
            whileHover={{ scale: 1.08, boxShadow: "0 0 20px rgba(0,196,255,0.45)" }}
            whileTap={{ scale: 0.96 }}
            onClick={() => window.dispatchEvent(new Event("openQueryForm"))}
          >
            Request a Proposal
          </CtaButton>
        </Section>

        <Grid>
          <Image src={loginImage} alt="Login Illustration" />

          <Card
            whileHover={{
              y: -10,
              scale: 1.03,
              boxShadow: "0 0 30px rgba(0,196,255,0.3)",
            }}
          >
            <CardTitle>Parinay sanskar</CardTitle>

            <CardDesc
              style={{
                maxHeight: showFullDesc ? 'none' : '200px',
                overflow: 'hidden',
              }}
            >
              1. Title = A clear, concise name of the case study.
              2. Executive Summary = A brief overview of the project: the client, the problem, the solution, and the results.
              3. Client Overview
              Name and brief description of the client (if applicable).
              Industry, size, and goals related to the project.
              4. Problem Statement / Project Challenge
              What business or technical problem needed to be solved?
              Key pain points and inefficiencies.
              Why was the change necessary?
              5. Project Objectives
              Clear list of goals the project aimed to achieve.
              (Use bullet points)
              Example:
              Reduce manual HR workload by 60%
              Implement centralized employee database
              Enable remote onboarding
              6. Scope of Work
              Detailed scope: what was included/excluded.
              Technologies involved.
              Modules or components developed.
              7. Solution Provided
              Description of the implemented solution.
              How the IT team approached the project.
              Architecture, technology stack, tools used (e.g., React, Java Spring Boot, Firebase, AWS, etc.)
              8. Implementation Details
              Project phases (e.g., planning, design, development, testing, deployment).
              Team composition and methodology (Agile, Scrum, etc.)
              Timeframe and milestones.
              9. Challenges Faced
              Technical or non-technical hurdles during the project.
              How the team resolved them.
              10. Results & Impact
              Quantitative/qualitative benefits delivered.
              Metrics, KPIs, or ROI.
              Before vs After comparison (use a table or infographic if needed).
              11. Client/Testimonial Feedback (optional)
              Quotes or feedback from the client or internal stakeholder.
              12. Conclusion
              Key takeaways.
              What was learned and future plans.
              13. Appendix (optional)
              Screenshots, architecture diagrams, code samples, charts, or Gantt chart.
            </CardDesc>

            <CardBtn
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowFullDesc(v => !v)}
            >
              {showFullDesc ? 'Show Less' : 'Continue Reading'}
            </CardBtn>
          </Card>
        </Grid>

      </Container>
    </Wrapper>
  );
}
